import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import { Tenant } from '../../../types/tenants';
import { useTenantsRequests } from '../../../api/tenants';
import { ERROR_SNACKBAR, SUCCESS_SNACKBAR } from '../../../consts';

type TenantStateAction = 'deactivate' | 'reactivate';

interface TenantStateProps {
  tenant?: Tenant;
  mode: TenantStateAction;
  onSuccess?: () => void;
}

const formatDate = (value?: string | null) => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString();
};

export const TenantState: React.FC<TenantStateProps> = ({ tenant, mode, onSuccess }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { loading, error, deactivate, reactivate } = useTenantsRequests();

  const { description, buttonLabel, action } = useMemo(() => {
    if (mode === 'deactivate') {
      return {
        description: t(
          'Deactivating the workspace will schedule its termination. You will retain access until the termination date.'
        ),
        buttonLabel: loading ? t('Processing...') : t('Confirm deactivation'),
        action: deactivate,
      };
    }

    return {
      description: t('Undoing the workspace deactivation will cancel the scheduled termination.'),
      buttonLabel: loading ? t('Processing...') : t('Confirm undo deactivation'),
      action: reactivate,
    };
  }, [mode, t, loading, deactivate, reactivate]);

  const handleSubmit = async () => {
    if (loading) {
      return;
    }

    const result = await action();
    if (result?.success) {
      window.dispatchEvent(new Event('tenantUpdated'));
      enqueueSnackbar(
        mode === 'deactivate'
          ? t('Workspace deactivated successfully')
          : t('Workspace deactivation undone successfully'),
        SUCCESS_SNACKBAR
      );
      onSuccess?.();
    } else {
      enqueueSnackbar(
        mode === 'deactivate'
          ? t('Workspace deactivation failed')
          : t('Workspace deactivation undo failed'),
        ERROR_SNACKBAR
      );
    }
  };

  if (!tenant) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const currentState = tenant.is_active ? t('Active') : t('Inactive');
  const terminationDate = formatDate(tenant.termination_date);

  const errorMessage =
    typeof error === 'string'
      ? error
      : error?.message ?? error?.detail ?? '';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>

      <Alert severity={tenant.is_active ? 'success' : 'warning'}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {t('Current state')}: {currentState}
        </Typography>
        {!tenant.is_active && terminationDate && (
          <Typography variant="body2">
            {t('Scheduled termination date')}: {terminationDate}
          </Typography>
        )}
      </Alert>

      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      <Button variant="contained" color={mode === 'deactivate' ? 'error' : 'success'} onClick={handleSubmit} disabled={loading}>
        {buttonLabel}
      </Button>
    </Box>
  );
};
